import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Typography
} from 'antd';
import { PLAN_DURATIONS } from 'constants/plans';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createTranslatorPlan,
  fetchAdminLanguages,
  updateTranslatorPlan
} from 'services/api/translatorPlans';
import {
  AdminLanguage,
  TranslatorPlan,
  TranslatorPlanInput
} from 'types/api/translatorPlans';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PATTERNS } from 'utils/validation';

const { TextArea } = Input;
const { Title } = Typography;

interface IProps {
  results: TranslatorPlan[];
  onSaved: () => void;
}

const TranslationPlansDetails: FC<IProps> = ({ results, onSaved }) => {
  // translations
  const { t } = useTranslation();
  // context
  const { translationPlans } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  // form
  const [form] = Form.useForm();

  const planId = translationPlans.params?.id;

  const [languages, setLanguages] = useState<AdminLanguage[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const closeModal = useCallback(() => {
    form.resetFields();
    toggleModal('translationPlans', false);
  }, [form, toggleModal]);

  useEffect(() => {
    if (!translationPlans.visible) return;
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchAdminLanguages(200);
        if (!cancelled) setLanguages(rows);
      } catch {
        // swallow — empty picker options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [translationPlans.visible]);

  // Apollo cache.readFragment is gone with the parent list off Apollo —
  // resolve the editing plan from the parent-owned results array instead.
  const planData = planId
    ? results.find(item => String(item.id) === String(planId)) ?? null
    : null;

  const handleSubmit = useCallback(
    async (values: TranslatorPlanInput) => {
      const input: TranslatorPlanInput = {
        ...values,
        price: Number(values.price),
        duration: Number(values.duration)
      };

      try {
        setIsSaving(true);

        if (planId) {
          await updateTranslatorPlan(planId, input);
        } else {
          await createTranslatorPlan(input);
        }

        onSaved();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    [closeModal, onSaved, planId]
  );

  useEffect(() => {
    if (translationPlans.visible && planData) {
      form.setFieldsValue({
        name: planData.name,
        price: planData.price,
        duration: planData.duration,
        lang_1: planData.lang_1,
        lang_2: planData.lang_2,
        description: planData.description
      });
    } else {
      form.resetFields();
    }
  }, [form, translationPlans.visible, planData]);

  return (
    <Modal
      open={translationPlans.visible}
      onCancel={closeModal}
      footer={
        <>
          <Divider />
          <Button onClick={closeModal}>{t('common.cancel')}</Button>
          <Button loading={isSaving} onClick={form.submit} type="primary">
            {t('common.save')}
          </Button>
        </>
      }
      title={
        <Title level={3} style={{ textAlign: 'center', marginBottom: 20 }}>
          {t(
            planId
              ? 'translation_plans.edit_plan'
              : 'translation_plans.add_plan'
          )}
        </Title>
      }
    >
      <Form form={form} layout="vertical" size="large" onFinish={handleSubmit}>
        <Form.Item
          name="name"
          label={t('translation_plans.name')}
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="price"
          label={t('translation_plans.price')}
          rules={[{ required: true }, { pattern: PATTERNS.price }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="duration"
          label={t('translation_plans.duration')}
          rules={[{ required: true }]}
        >
          <Select options={PLAN_DURATIONS} />
        </Form.Item>
        <Row gutter={[8, 0]}>
          <Col span={12}>
            <Form.Item
              name="lang_1"
              label={t('translation_plans.lang_1')}
              rules={[{ required: true }]}
            >
              <Select
                options={languages.map(item => ({
                  label: item.name,
                  value: item.code
                }))}
                optionFilterProp="label"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lang_2"
              label={t('translation_plans.lang_2')}
              rules={[{ required: true }]}
            >
              <Select
                options={languages.map(item => ({
                  label: item.name,
                  value: item.code
                }))}
                optionFilterProp="label"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="description"
          label={t('translation_plans.description')}
        >
          <TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(TranslationPlansDetails);
